import { MYNAVI_PRIMARY } from '@/components/Campaign/mynavi/Color'
import { MyNaviButton } from '@/components/Campaign/mynavi/MyNaviButton/MyNaviButton'
import { MyNaviTypography } from '@/components/Campaign/mynavi/MyNaviUI/MyNaviTypography'
import { VerificationCodeSchema } from '@/schemas/forms/emailRegistration'
import type { VerificationCodeFormData } from '@/schemas/forms/emailRegistration'
import { zodResolver } from '@hookform/resolvers/zod'
import { Modal } from '@mantine/core'
import { useForm } from 'react-hook-form'

type Props = {
  opened: boolean
  isLoading: boolean
  isResending: boolean
  errorMessage: string | null
  onSubmit: (code: string) => void
  onResend: () => void
  onBack: () => void
}

export const DialogEmailVerification = ({
  opened,
  isLoading,
  isResending,
  errorMessage,
  onSubmit,
  onResend,
  onBack,
}: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<VerificationCodeFormData>({
    resolver: zodResolver(VerificationCodeSchema),
  })

  const handleBack = () => {
    reset()
    onBack()
  }

  const handleFormSubmit = (data: VerificationCodeFormData) => {
    onSubmit(data.code)
  }

  return (
    <Modal
      opened={opened}
      onClose={handleBack}
      withCloseButton={!(isLoading || isResending)}
      closeOnClickOutside={!(isLoading || isResending)}
      closeOnEscape={!(isLoading || isResending)}
      centered
      size="md"
      radius="lg"
      lockScroll
      styles={{
        header: { justifyContent: 'center', paddingBottom: 8 },
        title: { flex: 1 },
        close: { '& svg': { strokeWidth: 3 } },
      }}
      title={
        <MyNaviTypography variant="dialog-title" as="p" className="text-center m-0" style={{ color: MYNAVI_PRIMARY }}>
          認証コード入力
        </MyNaviTypography>
      }
    >
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="flex flex-col gap-4 pb-2">
          <MyNaviTypography variant="search-hint" as="p" className="leading-[1.35] m-0">
            認証コードを送信しました。
            <br />
            メール本文の6桁の数字を入力し登録を完了してください。
          </MyNaviTypography>
          <div className="flex flex-col gap-1">
            <input
              id="verification-code-input"
              type="text"
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              aria-label="認証コード（6桁）"
              style={{
                border: '1px solid #686868',
                borderRadius: '5px',
                height: '42px',
                padding: '0 12px',
                fontSize: '16px',
                fontFamily: "'BIZ UDPGothic', sans-serif",
                width: '100%',
                backgroundColor: '#fbfbfb',
              }}
              {...register('code')}
            />
            {errors.code && <p className="text-xs text-red-500">{errors.code.message}</p>}
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onResend}
              disabled={isResending || isLoading}
              style={{
                fontSize: '15px',
                color: MYNAVI_PRIMARY,
                textDecoration: 'underline',
                fontFamily: "'BIZ UDPGothic', sans-serif",
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
              }}
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? '送信中...' : '認証コードを再送する'}
            </button>
          </div>
          {errorMessage && <p className="text-xs text-red-500 text-center">{errorMessage}</p>}
          <MyNaviButton
            label={isLoading ? '送信中...' : '送信'}
            showIcon={false}
            onClick={handleSubmit(handleFormSubmit)}
            fullWidth
          />
          <div className="flex justify-center">
            <button
              type="button"
              onClick={handleBack}
              disabled={isLoading || isResending}
              style={{
                fontSize: '15px',
                color: MYNAVI_PRIMARY,
                textDecoration: 'underline',
                fontFamily: "'BIZ UDPGothic', sans-serif",
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
              }}
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              戻る
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
